import type { WeeklyReportEmailTemplateProps } from "@/components/WeeklyReportEmailTemplate";
import WeeklyReportEmailTemplate from "@/components/WeeklyReportEmailTemplate";
import type { WeeklyReport, EmailDeliveryStatus } from "@/types/WeeklyReport";
import { gmailService } from "./gmailService";

/**
 * Email service for sending emails
 */
export const emailService = {
  /**
   * Send an email using Gmail API (main method)
   * This is the primary method that should be used for sending emails
   * Returns the delivery status with verification
   */
  sendEmail: async (report: WeeklyReport): Promise<EmailDeliveryStatus> => {
    const callId = `email-call-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    try {
      console.log(`[${callId}] EmailService - sendEmail method called`);
      console.log(
        `[${callId}] EmailService - Report sentByEmail flag:`,
        report.sentByEmail,
      );
      console.log(`[${callId}] EmailService - Report ID:`, report.id);
      console.log(`[${callId}] EmailService - Stack trace:`, new Error().stack);

      // Validate report
      if (!report) {
        throw new Error("No report provided");
      }

      // Validate recipients
      const hasRecipients =
        (Array.isArray(report.to) && report.to.length > 0) ||
        (typeof report.to === "string" && report.to !== "") ||
        (Array.isArray(report.cc) && report.cc.length > 0) ||
        (typeof report.cc === "string" && report.cc !== "") ||
        (Array.isArray(report.bcc) && report.bcc.length > 0) ||
        (typeof report.bcc === "string" && report.bcc !== "");

      console.log(`[${callId}] EmailService - Has recipients:`, hasRecipients);
      console.log(`[${callId}] EmailService - Recipients:`, {
        to: report.to,
        cc: report.cc,
        bcc: report.bcc,
      });

      if (!hasRecipients) {
        return {
          status: "failed",
          sentAt: new Date().toISOString(),
          error: "No recipients specified for email",
        };
      }

      // Check if Gmail is available and token is valid
      const gmailAvailable = await isGmailAvailable();
      console.log(
        `[${callId}] EmailService - Gmail access available:`,
        gmailAvailable,
      );

      if (!gmailAvailable) {
        return {
          status: "failed",
          sentAt: new Date().toISOString(),
          error:
            "Gmail access is not available. Please authenticate with Google first.",
        };
      }

      // Get valid access token, this will refresh if needed
      const validToken = await gmailService.getValidAccessToken();
      console.log(
        `[${callId}] EmailService - Valid token obtained:`,
        !!validToken,
      );

      if (!validToken) {
        return {
          status: "failed",
          sentAt: new Date().toISOString(),
          error:
            "Gmail token is invalid or expired. Please re-authenticate with Google.",
        };
      }

      // Ensure the report has a subject (required by WeeklyReport type)
      const reportWithDefaults: WeeklyReport = {
        ...report,
        subject: report.subject || "Weekly Report",
      };

      console.log(`[${callId}] EmailService - Sending email with Gmail API...`);

      // Use Gmail API to send the email and get the delivery status
      const deliveryStatus = await gmailService.sendEmail(reportWithDefaults);
      console.log(
        `[${callId}] EmailService - Gmail API response:`,
        deliveryStatus,
      );

      // Log to CloudWatch for debugging duplicate sends
      if (deliveryStatus.status === "sent" && deliveryStatus.messageId) {
        console.log(
          `[${callId}] EmailService - Logging email send to CloudWatch...`,
        );
        try {
          const { getJsonAuthHeaders } = await import("@/lib/apiHeaders");
          const logResponse = await fetch("/api/email/log", {
            method: "POST",
            headers: getJsonAuthHeaders(),
            credentials: "include",
            body: JSON.stringify({
              messageId: deliveryStatus.messageId,
              threadId: deliveryStatus.threadId,
              senderEmail: reportWithDefaults.createdBy || "unknown",
              recipients: {
                to: Array.isArray(reportWithDefaults.to)
                  ? reportWithDefaults.to
                  : reportWithDefaults.to
                    ? [reportWithDefaults.to]
                    : [],
                cc: Array.isArray(reportWithDefaults.cc)
                  ? reportWithDefaults.cc
                  : reportWithDefaults.cc
                    ? [reportWithDefaults.cc]
                    : [],
                bcc: Array.isArray(reportWithDefaults.bcc)
                  ? reportWithDefaults.bcc
                  : reportWithDefaults.bcc
                    ? [reportWithDefaults.bcc]
                    : [],
              },
              subject: reportWithDefaults.subject || "Weekly Report",
              reportId: reportWithDefaults.id,
              sendId: (deliveryStatus as any).sendId, // From gmailService
              callId: callId,
              status: deliveryStatus.status,
              error: deliveryStatus.error,
            }),
          });

          if (logResponse.ok) {
            console.log(
              `[${callId}] EmailService - Successfully logged to CloudWatch`,
            );
          } else {
            const errorText = await logResponse.text();
            console.warn(
              `[${callId}] EmailService - CloudWatch logging failed:`,
              logResponse.status,
              errorText,
            );
          }
        } catch (logError) {
          // Don't fail if logging fails
          console.warn(
            `[${callId}] EmailService - Failed to log to CloudWatch:`,
            logError,
          );
        }
      } else {
        console.log(
          `[${callId}] EmailService - Skipping CloudWatch log (status: ${deliveryStatus.status}, messageId: ${deliveryStatus.messageId})`,
        );
      }

      // Return the status directly - Gmail acceptance doesn't guarantee delivery
      // but it's the best we can confirm without additional scopes
      return deliveryStatus;
    } catch (error) {
      console.error(`[${callId}] EmailService - Error sending email:`, error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      return {
        status: "failed",
        sentAt: new Date().toISOString(),
        error: `Failed to send email: ${errorMessage}`,
      };
    }
  },

  /**
   * Send an email using Gmail API
   */
  sendEmailWithGmail: async (report: WeeklyReport): Promise<void> => {
    try {
      console.log("EmailService - Sending email with Gmail:", report);

      // Check if Gmail is available and token is valid
      if (!(await isGmailAvailable())) {
        throw new Error(
          "Gmail access is not available. Please authenticate with Google first.",
        );
      }

      // Get valid token before sending
      const validToken = await gmailService.getValidAccessToken();
      if (!validToken) {
        throw new Error(
          "Gmail token is invalid or expired. Please re-authenticate with Google.",
        );
      }

      // Send email with Gmail API
      await gmailService.sendEmail(report);

      console.log("EmailService - Email sent successfully with Gmail");
    } catch (error) {
      console.error("EmailService - Error sending email with Gmail:", error);
      throw error;
    }
  },

  /**
   * Generate plain text email for copying
   */
  generatePlainTextEmail: (
    report: WeeklyReport | WeeklyReportEmailTemplateProps,
  ): string => {
    return generatePlainTextEmail(report);
  },

  /**
   * Check if Gmail is available
   */
  isGmailAvailable: async (): Promise<boolean> => {
    return await isGmailAvailable();
  },
};

/**
 * Generate plain text email
 */
function generatePlainTextEmail(
  report: WeeklyReport | WeeklyReportEmailTemplateProps,
): string {
  // If it's a WeeklyReport, convert to template props format
  if ("createdBy" in report && !("name" in report)) {
    // This is a WeeklyReport
    const to = Array.isArray(report.to)
      ? report.to.join(", ")
      : report.to || "";
    const cc = Array.isArray(report.cc)
      ? report.cc.join(", ")
      : report.cc || "";
    const bcc = Array.isArray(report.bcc)
      ? report.bcc.join(", ")
      : report.bcc || "";

    const props: WeeklyReportEmailTemplateProps = {
      name: report.createdBy || "Unknown User",
      dateRange: report.dateRange || report.date,
      last_week_tasks: report.last_week_tasks || [],
      Unplanned: report.Unplanned || [],
      blockers: report.blockers || [],
      next_week_tasks: report.next_week_tasks || [],
      further_beyond: report.further_beyond || [],
      notes: report.notes,
      greeting: report.greeting,
      signature: report.signature,
      to,
      cc,
      bcc,
    };
    return WeeklyReportEmailTemplate(props);
  }

  // This is already in WeeklyReportEmailTemplateProps format
  return WeeklyReportEmailTemplate(report as WeeklyReportEmailTemplateProps);
}

/**
 * Check if Gmail is available
 */
async function isGmailAvailable(): Promise<boolean> {
  try {
    return await gmailService.hasAccessToken();
  } catch (error) {
    console.error("EmailService - Error checking Gmail availability:", error);
    return false;
  }
}
